import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  Shield,
  Brain,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import Badge from '../ui/badge';

const ExecutiveSummary = ({ report }) => {
  if (!report) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">No report data available</p>
        </CardContent>
      </Card>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    if (score >= 70) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (score >= 80) return <Minus className="w-4 h-4 text-yellow-600" />;
    return <TrendingDown className="w-4 h-4 text-red-600" />;
  };

  const getRiskBadgeVariant = (riskLevel) => {
    switch (riskLevel) {
      case 'LOW': return 'default';
      case 'MEDIUM': return 'secondary';
      case 'HIGH': return 'destructive';
      default: return 'outline';
    }
  };

  const getPerformanceGradeBadgeVariant = (grade) => {
    if (grade === 'A') return 'default';
    if (grade === 'B') return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      {/* Overall Score Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Overall Performance</span>
            <div className="flex items-center space-x-2">
              {getScoreIcon(report.overall_score)}
              <span className={`text-2xl font-bold ${getScoreColor(report.overall_score)}`}>
                {report.overall_score}%
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-secondary/50 p-4 rounded-lg">
            <p className="text-foreground">{report.summary}</p>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Accuracy */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Accuracy</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-2xl font-bold ${getScoreColor(report.metrics.accuracy.score)}`}>
                    {report.metrics.accuracy.score}%
                  </span>
                  {getScoreIcon(report.metrics.accuracy.score)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {report.metrics.accuracy.accurate_responses}/{report.metrics.accuracy.total_responses} responses
                </p>
              </div>
              <Target className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        {/* Latency */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Response Time</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-2xl font-bold text-blue-600">
                    {report.metrics.latency.average_ms}ms
                  </span>
                  <Badge variant={getPerformanceGradeBadgeVariant(report.metrics.latency.performance_grade)}>
                    {report.metrics.latency.performance_grade}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  P95: {report.metrics.latency.p95_ms}ms
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Security</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-2xl font-bold ${getScoreColor(report.metrics.security.score)}`}>
                    {report.metrics.security.score}%
                  </span>
                  <Badge variant={getRiskBadgeVariant(report.metrics.security.risk_level)}>
                    {report.metrics.security.risk_level}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {report.metrics.security.vulnerabilities_found} vulnerabilities
                </p>
              </div>
              <Shield className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        {/* Reliability */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Reliability</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-2xl font-bold ${getScoreColor(report.metrics.hallucination.factual_accuracy_score)}`}>
                    {report.metrics.hallucination.factual_accuracy_score}%
                  </span>
                  {getScoreIcon(report.metrics.hallucination.factual_accuracy_score)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {report.metrics.hallucination.hallucination_rate}% hallucination rate
                </p>
              </div>
              <Brain className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Content Safety Status */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              {report.metrics.offense.offensive_content_detected === 0 ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-red-500" />
              )}
              <div>
                <p className="font-medium">Content Safety</p>
                <p className="text-sm text-muted-foreground">
                  {report.metrics.offense.offensive_content_detected === 0
                    ? 'No offensive content detected'
                    : `${report.metrics.offense.offensive_content_detected} issues found`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Health Status */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              {report.metrics.summary.failed_responses === 0 ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
              <div>
                <p className="font-medium">System Health</p>
                <p className="text-sm text-muted-foreground">
                  {report.metrics.summary.successful_responses}/{report.metrics.summary.total_responses} successful
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Status */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              {report.metrics.security.compliance_score >= 90 ? (
                <CheckCircle className="w-8 h-8 text-green-500" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              )}
              <div>
                <p className="font-medium">Compliance</p>
                <p className="text-sm text-muted-foreground">
                  {report.metrics.security.compliance_score}% compliant
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Key Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(report.recommendations).slice(0, 3).map(([category, items]) => (
              <div key={category} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium capitalize">{category}</p>
                  <p className="text-sm text-muted-foreground">{items[0]}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutiveSummary;
